import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import { BORDER_GRAY, WHITE } from '../../theme';
import { Divider } from '../reusable/Divider';
import Text, { TextColor, TextKind } from '../reusable/Text';
import Tooltip from '../reusable/Tooltip';

const StyledSettingsContainer = styled.div`
  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;
  background: ${WHITE};
  padding: 40px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledHeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface SettingsContainerProps extends React.PropsWithChildren {
  title: string;
  subtitle: string;
  gap?: number;
}

export const SettingsPage: React.FC<SettingsContainerProps> = ({
  title,
  subtitle,
  children,
  gap = 0,
}) => (
  <StyledSettingsContainer style={{ gap }}>
    <StyledHeadingContainer>
      <Text component="h2" kind={TextKind.TitleM}>
        {title}
      </Text>
      <Text kind={TextKind.BodyL} color={TextColor.Secondary}>
        {subtitle}
      </Text>
    </StyledHeadingContainer>
    <Divider $marginBottom={32} $marginTop={32} />
    {children}
  </StyledSettingsContainer>
);

const StyledSettingsSectionHeader = styled.div`
  margin-bottom: 32px;
`;

export const SettingsSectionHeader: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <StyledSettingsSectionHeader>
    <Text kind={TextKind.TitleS}>{children}</Text>
  </StyledSettingsSectionHeader>
);

const StyledSettingsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 16px;

  margin-bottom: 32px;
`;

interface SettingsSectionProps extends React.PropsWithChildren {
  title: string;
  subtitle?: string;
  icon?: IconDefinition;
  iconTooltip?: string;
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  subtitle,
  children,
  icon,
  iconTooltip,
}) => (
  <StyledSettingsSection>
    <StyledHeadingContainer>
      <Text component="h3" kind={TextKind.TitleXs}>
        {title}
        {icon && iconTooltip && (
          <Tooltip content={iconTooltip || ''} placement="top">
            <FontAwesomeIcon
              style={{
                width: 16,
                height: 16,
                marginLeft: '8px',
              }}
              icon={icon}
            />
          </Tooltip>
        )}
      </Text>
      {subtitle && (
        <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
          {subtitle}
        </Text>
      )}
    </StyledHeadingContainer>
    {children}
  </StyledSettingsSection>
);
